// Rotating fact card shown on /login (and reusable on the landing page).
// Same framed-specimen look as the original Specimen component, but the
// content is a curated set of value-prop facts instead of a fictional user.
import { useEffect, useRef, useState } from "react";

interface Fact {
  badge: string;          // small mono caps label, e.g. "01 — STAT"
  headline: string;       // big serif statement
  body?: string;          // optional supporting prose
  attribution?: string;   // optional small italic credit line
}

const FACTS: Fact[] = [
  {
    badge: "01 — Stat",
    headline: "73% of business cards are thrown out within a week.",
    attribution: "— Statistic Brain Research Institute",
  },
  {
    badge: "02 — Why",
    headline: "A card that updates itself.",
    body:
      "Change your role, photo, or phone once — every link already in the wild updates.",
  },
  {
    badge: "03 — How",
    headline: "Two formats. One link.",
    body:
      "Open varo.biz/your-handle in any browser. Drop the link in a chat — the preview unfolds.",
  },
  {
    badge: "04 — Range",
    headline: "For freelancers, founders, designers, doctors.",
    body:
      "Anyone who hands their contact details to a stranger more than twice a year.",
  },
  {
    badge: "05 — The link",
    headline: "varo.biz/your-handle",
    body: "Short enough to say. Long enough to keep.",
  },
];

const ROTATE_MS = 6000;

export function FactSpecimen() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = window.setInterval(() => {
      setIdx((i) => (i + 1) % FACTS.length);
    }, ROTATE_MS);
    return () => {
      if (intervalRef.current !== undefined) window.clearInterval(intervalRef.current);
    };
  }, [paused]);

  const fact = FACTS[idx];

  return (
    <div
      className="rise d2 max-w-md rounded-lg border border-white/25 bg-white/[0.04] p-8 backdrop-blur-sm"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ minHeight: 280 }}
    >
      <span className="eyebrow text-paper/50">{fact.badge}</span>
      <h2
        key={idx}                /* re-mount triggers the rise animation */
        className="rise mt-4 font-display text-4xl leading-[1.05] tracking-tight text-paper [font-variation-settings:'opsz'_96,'SOFT'_30,'WONK'_1,'wght'_400]"
      >
        {fact.headline}
      </h2>
      {fact.body && (
        <p key={`b${idx}`} className="rise d1 mt-5 text-base leading-relaxed text-paper/70">
          {fact.body}
        </p>
      )}
      {fact.attribution && (
        <p
          key={`a${idx}`}
          className="rise d2 mt-5 font-display italic text-sm text-paper/55 [font-variation-settings:'opsz'_18,'SOFT'_60]"
        >
          {fact.attribution}
        </p>
      )}

      {/* Pagination dots */}
      <div className="mt-8 flex gap-2">
        {FACTS.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Show fact ${i + 1}`}
            onClick={() => {
              setIdx(i);
              setPaused(true);
              window.setTimeout(() => setPaused(false), 8000);
            }}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-8 bg-terra" : "w-1.5 bg-paper/30 hover:bg-paper/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
